import { useState, useEffect, useRef } from "react";
import {
    Form,
    Link,
    useActionData,
    useRouteLoaderData,
    useSubmit,
} from "react-router-dom";
import Cleave from "cleave.js/react";

import { states, cityStates } from "src/components/common/cityState";
import { Organization } from "./types";
import { useModalStore } from "../main/modalStore";

export default function OrganizationForm({
    keyword
}: {
    keyword: "Add" | "Edit";
}) {
    const actionData = useActionData();
    const loaderOrg =
    useRouteLoaderData("organization")?.organization ?? {
        id: "",
        name: "",
        website: "",
        phoneNumber: "",
        extension: "",
        fax: "",
        street: "",
        city: "",
        state: "",
        zipCode: "",
        overseaAddress: "",
        background: ""
    };
    const [formValues, setFormValues] = useState(loaderOrg);
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormValues((prev:Organization)=> ({
            ...prev,
            [name]: value
        }));
    };
    const submit = useSubmit();
    const formRef = useRef(null);
    useEffect(() => {
        if(actionData?.duplicate){
            useModalStore.getState().showModal({
                title: "Warning",
                message: "Potential Duplicates:",
                organizations: actionData.data
            });
            useModalStore.getState().modal!.onConfirm = async () => {
                const newForm = new FormData(formRef.current!);
                newForm.append("force", "1");
                submit(newForm, { method: "post" });
            }
        }
    }, [actionData, submit])
    return (
        <Form method="post" ref = {formRef}>
            <input type="hidden" name="action" value={keyword} />
            <input type="hidden" name="id" value={formValues.id ?? ""} />
            <div className="cssSeperateWhenWide ps-3 mb-2">
                <div className="flex-grow-1 me-3">
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="name">
                            Organization Name:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            required
                            id="name"
                            name="name"
                            value={formValues.name ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="website">Website:</label>
                        <input
                            type="url"
                            className="form-control flex-grow-1 ms-3"
                            id="website"
                            name="website"
                            value={formValues.website ?? ""}
                            onChange = {handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="phoneNumber">Business Phone:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formValues.phoneNumber ?? ""}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    phoneNumber: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="extension">Extension:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="extension"
                            name="extension"
                            value={formValues.extension ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="fax">Fax:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="fax"
                            name="fax"
                            value={formValues.fax ?? ""}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    fax: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                </div>
                <div className="flex-grow-1 me-3">
                    <h4 className = "fw-bold text-center mt-2">Address</h4>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="street">Street:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="street"
                            name="street"
                            value={formValues.street ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="city">City:</label>
                        <select
                            className="form-control flex-grow-1 ms-3"
                            id="city"
                            name="city"
                            value={formValues.city ?? ""}
                            onChange={handleChange}
                        >
                            <option value="">Select</option>
                            {formValues.state &&
                                cityStates[formValues.state as keyof typeof cityStates]?.map(
                                    (s, i) => <option key={i}>{s}</option>
                                )}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="state">State:</label>
                        <select
                            className = "form-control flex-grow-1 ms-3"
                            id="state"
                            name = "state"
                            value = {formValues.state ?? ""}
                            onChange = {handleChange}
                        >
                            <option value="">Select</option>
                            {states.map((s, i) => (
                                <option key={i}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="zipCode">Zip Code:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="zipCode"
                            name="zipCode"
                            value={formValues.zipCode ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="overseaAddress">Oversea Address:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="overseaAddress"
                            name="overseaAddress"
                            value={formValues.overseaAddress ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="d-flex flex-column align-items-start mt-2">
                    <label className="fw-bold mb-0" htmlFor="background">Background:</label>
                    <textarea
                        className="form-control w-100 flex-grow-1"
                        id="background"
                        name="background"
                        value={formValues.background ?? ""}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <div className="my-3">
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {keyword}
                </button>
                <Link
                    to=".."
                    className="btn btn-outline-secondary float-end"
                >
                    Back
                </Link>
            </div>
        </Form>
    );
}
